import { Link, useParams } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import {
  ArrowLeft,
  Bookmark,
  Building2,
  CalendarRange,
  CheckSquare,
  ClipboardList,
  DollarSign,
  FileStack,
  FileText,
  FolderTree,
  HardHat,
  Layers,
  Milestone,
  Pencil,
  Percent,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
  Users,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useConfigItems,
  useCreateConfigItem,
  useDeleteConfigItem,
  useUpdateConfigItem,
} from "@/hooks/use-config";

// ---------------------------------------------------------------------------
// Field definitions per config type
// ---------------------------------------------------------------------------

interface FieldDef {
  key: string;
  label: string;
  type: "text" | "number" | "boolean" | "select";
  required?: boolean;
  options?: { value: string; label: string }[];
}

const STANDARD_FIELDS: FieldDef[] = [
  { key: "name", label: "Name", type: "text", required: true },
  { key: "description", label: "Description", type: "text" },
  { key: "sortOrder", label: "Sort Order", type: "number" },
  { key: "isActive", label: "Active", type: "boolean" },
];

const CONFIG_TYPE_FIELDS: Record<string, FieldDef[]> = {
  "project-phases": STANDARD_FIELDS,
  "submittal-types": STANDARD_FIELDS,
  "rfi-categories": STANDARD_FIELDS,
  "document-categories": STANDARD_FIELDS,
  "company-types": STANDARD_FIELDS,
  "retention-rates": [
    { key: "name", label: "Name", type: "text", required: true },
    { key: "rate", label: "Rate (%)", type: "number", required: true },
    { key: "description", label: "Description", type: "text" },
    { key: "isDefault", label: "Default", type: "boolean" },
    { key: "isActive", label: "Active", type: "boolean" },
  ],
  "markup-fees": [
    { key: "name", label: "Name", type: "text", required: true },
    {
      key: "rate",
      label: "Rate (%)",
      type: "number",
      required: true,
    },
    {
      key: "feeType",
      label: "Fee Type",
      type: "select",
      options: [
        { value: "overhead", label: "Overhead" },
        { value: "profit", label: "Profit" },
        { value: "insurance", label: "Insurance" },
        { value: "bond", label: "Bond" },
        { value: "tax", label: "Tax" },
        { value: "other", label: "Other" },
      ],
    },
    { key: "description", label: "Description", type: "text" },
    { key: "isActive", label: "Active", type: "boolean" },
  ],
  "trade-categories": [
    { key: "name", label: "Name", type: "text", required: true },
    { key: "description", label: "Description", type: "text" },
    { key: "csiDivision", label: "CSI Division", type: "text" },
    { key: "sortOrder", label: "Sort Order", type: "number" },
    { key: "isActive", label: "Active", type: "boolean" },
  ],
  "checklist-templates": [
    { key: "name", label: "Name", type: "text", required: true },
    { key: "description", label: "Description", type: "text" },
    { key: "category", label: "Category", type: "text" },
    { key: "isActive", label: "Active", type: "boolean" },
  ],
  "user-roles": [
    { key: "name", label: "Name", type: "text", required: true },
    { key: "description", label: "Description", type: "text" },
    { key: "permissions", label: "Permissions", type: "text" },
    { key: "sortOrder", label: "Sort Order", type: "number" },
    { key: "isActive", label: "Active", type: "boolean" },
  ],
  "contact-groups": [
    { key: "name", label: "Name", type: "text", required: true },
    { key: "description", label: "Description", type: "text" },
  ],
};

// ---------------------------------------------------------------------------
// Config section definitions for the hub grid
// ---------------------------------------------------------------------------

interface ConfigSection {
  key: string;
  title: string;
  description: string;
  icon: LucideIcon;
}

const configSections: ConfigSection[] = [
  {
    key: "cost-codes",
    title: "Cost Codes",
    description: "Manage project cost codes and categories",
    icon: DollarSign,
  },
  {
    key: "task-templates",
    title: "Task Templates",
    description: "Define reusable task templates for projects",
    icon: ClipboardList,
  },
  {
    key: "milestone-templates",
    title: "Milestone Templates",
    description: "Configure milestone templates for project timelines",
    icon: Milestone,
  },
  {
    key: "folder-templates",
    title: "Folder Templates",
    description: "Set up default folder structures for new projects",
    icon: FolderTree,
  },
  {
    key: "gc-line-item-templates",
    title: "GC Line Item Templates",
    description: "Reusable GC-owned cost baselines for new projects",
    icon: FileStack,
  },
  {
    key: "billing-periods",
    title: "Billing Periods",
    description: "Manage AIA billing periods used by pay applications",
    icon: CalendarRange,
  },
  {
    key: "project-team-roles",
    title: "Project Team Roles",
    description: "Define the roles available when assigning people to a project",
    icon: Users,
  },
  {
    key: "project-phases",
    title: "Project Phases",
    description: "Define the phases of a construction project",
    icon: Layers,
  },
  {
    key: "submittal-types",
    title: "Submittal Types",
    description: "Configure types for project submittals",
    icon: FileText,
  },
  {
    key: "rfi-categories",
    title: "RFI Categories",
    description: "Manage categories for requests for information",
    icon: Bookmark,
  },
  {
    key: "retention-rates",
    title: "Retention Rates",
    description: "Set retention rates for pay applications",
    icon: Percent,
  },
  {
    key: "trade-categories",
    title: "Trade Categories",
    description: "Organize trades by CSI division and category",
    icon: HardHat,
  },
  {
    key: "checklist-templates",
    title: "Checklist Templates",
    description: "Create reusable checklist templates",
    icon: CheckSquare,
  },
  {
    key: "document-categories",
    title: "Document Categories",
    description: "Categorize project documents",
    icon: FolderTree,
  },
  {
    key: "markup-fees",
    title: "Markup & Fees",
    description: "Configure overhead, profit, and fee percentages",
    icon: DollarSign,
  },
  {
    key: "company-types",
    title: "Company Types",
    description: "Define company classification types",
    icon: Building2,
  },
  {
    key: "user-roles",
    title: "User Roles",
    description: "Manage user roles and permissions",
    icon: ShieldCheck,
  },
  {
    key: "contact-groups",
    title: "Contact Groups",
    description: "Organize contacts into groups",
    icon: Users,
  },
];

// Sections that have their own dedicated route
const DEDICATED_ROUTE_SECTIONS = new Set([
  "cost-codes",
  "task-templates",
  "milestone-templates",
  "folder-templates",
  "gc-line-item-templates",
  "billing-periods",
  "project-team-roles",
]);

// ---------------------------------------------------------------------------
// ConfigFormDialog
// ---------------------------------------------------------------------------

function buildDefaultValues(fields: FieldDef[]): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const f of fields) {
    if (f.type === "boolean") {
      values[f.key] = f.key === "isActive";
    } else if (f.type === "number") {
      values[f.key] = "";
    } else {
      values[f.key] = "";
    }
  }
  return values;
}

function ConfigFormDialog({
  open,
  onOpenChange,
  fields,
  initialValues,
  title,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fields: FieldDef[];
  initialValues: Record<string, unknown> | null;
  title: string;
  onSubmit: (values: Record<string, unknown>) => void;
  isPending: boolean;
}) {
  const [formValues, setFormValues] = useState<Record<string, unknown>>(
    () => initialValues ?? buildDefaultValues(fields),
  );

  // Reset form when dialog opens with new values
  const [lastOpen, setLastOpen] = useState(false);
  if (open && !lastOpen) {
    setFormValues(initialValues ?? buildDefaultValues(fields));
  }
  if (open !== lastOpen) {
    setLastOpen(open);
  }

  const handleFieldChange = (key: string, value: unknown) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Convert number fields
    const processed: Record<string, unknown> = {};
    for (const f of fields) {
      const val = formValues[f.key];
      if (f.type === "number" && typeof val === "string" && val !== "") {
        processed[f.key] = Number(val);
      } else {
        processed[f.key] = val;
      }
    }
    onSubmit(processed);
  };

  const missingRequired = fields.some(
    (f) => f.required && (formValues[f.key] === "" || formValues[f.key] == null),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {fields.map((field) => (
            <div key={field.key} className="space-y-2">
              {field.type === "boolean" ? (
                <div className="flex items-center justify-between">
                  <Label htmlFor={field.key}>{field.label}</Label>
                  <Switch
                    id={field.key}
                    checked={Boolean(formValues[field.key])}
                    onCheckedChange={(checked) => handleFieldChange(field.key, checked)}
                  />
                </div>
              ) : (
                <>
                  <Label htmlFor={field.key}>
                    {field.label}
                    {field.required && <span className="ml-1 text-destructive">*</span>}
                  </Label>
                  {field.type === "select" && field.options ? (
                    <Select
                      value={String(formValues[field.key] ?? "")}
                      onValueChange={(v) => handleFieldChange(field.key, v)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {field.options.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id={field.key}
                      type={field.type === "number" ? "number" : "text"}
                      value={String(formValues[field.key] ?? "")}
                      onChange={(e) => handleFieldChange(field.key, e.target.value)}
                      placeholder={field.label}
                    />
                  )}
                </>
              )}
            </div>
          ))}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || missingRequired}>
              {isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// ConfigPage – CRUD table for a single config type
// ---------------------------------------------------------------------------

function ConfigPage({ sectionKey }: { sectionKey: string }) {
  const section = configSections.find((s) => s.key === sectionKey);
  const title = section?.title ?? sectionKey;
  const fields = CONFIG_TYPE_FIELDS[sectionKey] ?? STANDARD_FIELDS;

  const { data: items, isLoading, isError, error, refetch } = useConfigItems(sectionKey);
  const createMutation = useCreateConfigItem(sectionKey);
  const updateMutation = useUpdateConfigItem(sectionKey);
  const deleteMutation = useDeleteConfigItem(sectionKey);

  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState<Record<string, unknown> | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  // Visible columns (non-boolean fields except id)
  const tableColumns = fields.filter((f) => f.type !== "boolean");
  const booleanColumns = fields.filter((f) => f.type === "boolean");

  const filteredItems = (items ?? []).filter((item) => {
    if (!search) return true;
    const lowerSearch = search.toLowerCase();
    return fields.some((f) => {
      const val = item[f.key];
      return typeof val === "string" && val.toLowerCase().includes(lowerSearch);
    });
  });

  const handleCreate = (values: Record<string, unknown>) => {
    createMutation.mutate(values, {
      onSuccess: () => {
        toast.success(`${title} item created`);
        setCreateOpen(false);
      },
      onError: (err) => {
        toast.error(err.message);
      },
    });
  };

  const handleUpdate = (values: Record<string, unknown>) => {
    if (!editItem) return;
    const id = String(editItem.id ?? "");
    updateMutation.mutate(
      { id, ...values },
      {
        onSuccess: () => {
          toast.success(`${title} item updated`);
          setEditItem(null);
        },
        onError: (err) => {
          toast.error(err.message);
        },
      },
    );
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success(`${title} item deleted`);
        setDeleteTarget(null);
      },
      onError: (err) => {
        toast.error(err.message);
      },
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={title}
        description={section?.description ?? "Configuration section"}
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add
            </Button>
            <Button variant="outline" asChild>
              <Link to="/admin/config">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Link>
            </Button>
          </div>
        }
      />

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Loading */}
      {isLoading && (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <p className="text-sm text-muted-foreground">Loading...</p>
          </CardContent>
        </Card>
      )}

      {/* Error */}
      {isError && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-3 py-12">
            <p className="text-sm text-destructive">
              {error instanceof Error ? error.message : "Failed to load data"}
            </p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Empty */}
      {!isLoading && !isError && filteredItems.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-sm text-muted-foreground">
              {search ? "No items match your search." : "No items yet. Click Add to create one."}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      {!isLoading && !isError && filteredItems.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  {tableColumns.map((col) => (
                    <TableHead key={col.key}>{col.label}</TableHead>
                  ))}
                  {booleanColumns.map((col) => (
                    <TableHead key={col.key}>{col.label}</TableHead>
                  ))}
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map((item) => {
                  const itemId = String(item.id ?? "");
                  return (
                    <TableRow key={itemId}>
                      {tableColumns.map((col) => (
                        <TableCell key={col.key}>
                          {item[col.key] != null ? String(item[col.key]) : "-"}
                        </TableCell>
                      ))}
                      {booleanColumns.map((col) => (
                        <TableCell key={col.key}>{item[col.key] ? "Yes" : "No"}</TableCell>
                      ))}
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditItem(item)}
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              setDeleteTarget({
                                id: itemId,
                                name: String(item.name ?? "this item"),
                              })
                            }
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Create dialog */}
      <ConfigFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        fields={fields}
        initialValues={null}
        title={`Add ${title}`}
        onSubmit={handleCreate}
        isPending={createMutation.isPending}
      />

      {/* Edit dialog */}
      <ConfigFormDialog
        open={editItem !== null}
        onOpenChange={(open) => {
          if (!open) setEditItem(null);
        }}
        fields={fields}
        initialValues={editItem}
        title={`Edit ${title}`}
        onSubmit={handleUpdate}
        isPending={updateMutation.isPending}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="Delete Item"
        description={`Are you sure you want to delete "${deleteTarget?.name ?? ""}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        loading={deleteMutation.isPending}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// ConfigGrid – hub view showing all config sections
// ---------------------------------------------------------------------------

function ConfigGrid() {
  const [search, setSearch] = useState("");

  const lowerSearch = search.trim().toLowerCase();
  const filteredSections = lowerSearch
    ? configSections.filter(
        (s) =>
          s.title.toLowerCase().includes(lowerSearch) ||
          s.description.toLowerCase().includes(lowerSearch) ||
          s.key.toLowerCase().includes(lowerSearch),
      )
    : configSections;

  return (
    <div className="space-y-6">
      <PageHeader title="Configuration" description="System-wide settings and templates" />

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search configuration sections..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {filteredSections.length === 0 ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <p className="text-sm text-muted-foreground">
              No configuration sections match your search.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredSections.map((section) => (
            <Link
              key={section.key}
              to="/admin/config/$section"
              params={{ section: section.key }}
              className="group"
            >
              <Card className="h-full transition-colors group-hover:border-primary/50">
                <CardHeader className="flex flex-row items-center gap-3 pb-2">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <section.icon className="h-5 w-5" />
                  </div>
                  <CardTitle className="text-base">{section.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{section.description}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Exported route components
// ---------------------------------------------------------------------------

export function AdminConfigPage() {
  const { section } = useParams({ strict: false });

  if (section) {
    // Sections with dedicated routes are handled by the router directly
    if (DEDICATED_ROUTE_SECTIONS.has(section)) {
      return <ConfigGrid />;
    }
    return <ConfigPage sectionKey={section} />;
  }

  return <ConfigGrid />;
}

export function AdminConfigSectionPage() {
  const { section } = useParams({ strict: false });
  const sectionKey = section ?? "";

  if (DEDICATED_ROUTE_SECTIONS.has(sectionKey)) {
    // These are handled by their own dedicated route components
    return <ConfigGrid />;
  }

  return <ConfigPage sectionKey={sectionKey} />;
}
